import type {
  ConceptWorksheetFilterBody,
  MergedWorksheetFilterBody,
  MyDbOriginalWorksheetFilterBody,
  MyDbSimilarWorksheetFilterBody,
  PracticeTestWorksheetFilterBody,
  PublicWorkbookWorksheetFilterBody,
  SchoolTestWorksheetFilterBody,
} from '@mathflat/domain/MakeWorksheet'
import type { StudentWorkbookScoredProblemDetail } from '@mathflat/domain/StudentWorkbook'
import type { Workbook } from '@mathflat/domain/Workbook'
import { AxiosError } from 'axios'
import { makeAutoObservable } from 'mobx'

import { MAX_PROBLEM_COUNT } from '@/constant'
import { worksheetController } from '@/controller/worksheet.controller'

/**
 * @deprecated
 * BE와 소통하는 filter 제거하면서, 교재쪽에서 사용하지 않게 됨. 앞으로 학습지도 삭제하는 방향으로
 */
export class MakeLessonMaterialFilterService<
  U extends 필터아이디필터,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  R extends Record<string, any> = { problemCount: number },
  E extends { message?: string; type: string } = { message?: string; type: 'overflow' },
> {
  request: U
  response?: ({ status: 'success'; error?: never } & R) | { status: 'error'; error: E } // 추후 필요하면 errorType 외부에서 받을 수 있도록

  requestAsync?: (request: U) => Promise<string>
  private _responseAsync: (
    filterId: string,
  ) => Promise<MakeLessonMaterialFilterService<U, R, E>['response']>

  constructor(data: {
    request: U
    requestAsync?: MakeLessonMaterialFilterService<U, R, E>['requestAsync']
    responseAsync?: (
      filterId: string,
    ) => Promise<MakeLessonMaterialFilterService<U, R, E>['response']>
  }) {
    makeAutoObservable(this)
    this.request = data.request
    this.requestAsync = data.requestAsync
    this._responseAsync =
      data.responseAsync ??
      (async (filterId: string) => {
        try {
          const problemCount = await worksheetController.fetchProblemCountByFilterId(filterId)
          return { status: 'success', problemCount }
        } catch (e) {
          if (e instanceof AxiosError && e.code === 'ERR_CANCELED') {
            throw e
          }
          return {
            status: 'error',
            error: { type: 'overflow' } as E, // 왜 추론을 못할까..
          }
        }
      })
  }

  get isValid() {
    // 기존 출제 문제 제외를 '학생 기준'으로 골랐는데 대상 학생이 없으면 출제 불가
    const request = this.request as Partial<공통지원교재필터>
    if (
      request.excludePrevious &&
      request.previousExclusionScope === 'STUDENT_ASSIGNED' &&
      !request.studentIds?.length
    ) {
      return false
    }
    if (!this.response) return true
    if (this.response.status === 'error') {
      return false
    } else {
      return this.response.problemCount > 0 && this.response.problemCount <= MAX_PROBLEM_COUNT
    }
  }

  updateAsync = async (request: U, opt?: { skipResponse?: boolean }) => {
    if (!this.requestAsync) return
    const filterId = await this.requestAsync(request)
    this.request = { ...request, filterId }
    if (opt?.skipResponse) return
    const response = await this._responseAsync(filterId)
    this.response = response
  }
}

export type 필터아이디필터 = { filterId: string | null }

// 화면이 들고 다니는 필터 상태. 요청 타입은 서버를 따라 nullable·생략 가능 필드가 많지만, 화면은 아래 값을 늘 채워 둔다
type _Concept = ConceptWorksheetFilterBody
type _PublicWorkbook = PublicWorkbookWorksheetFilterBody
export type 문제수필터 = Pick<_Concept, 'problemCount'>
export type 난이도필터 = { level: NonNullable<_Concept['level']> }
type 난이도설정필터 = { levelWeight: number[] }

export type 문제타입필터 = {
  problemFilterType: NonNullable<_Concept['problemFilterType']>
  onlyAutoScorable: _Concept['onlyAutoScorable']
}
export type 모의고사포함여부필터 = { practiceTest: NonNullable<_Concept['practiceTest']> }
export type 기존출제문제제외필터 = { excludePrevious: boolean }
export type 기존출제문제제외기준필터 = {
  previousExclusionScope?: NonNullable<_PublicWorkbook['previousExclusionScope']>
  studentIds?: NonNullable<NonNullable<_PublicWorkbook['studentIds']>[number]>[]
}
export type 교육과정외유형제외필터 = Pick<_Concept, 'excludeOOC'>
type 문제수균등배분필터 = Pick<_Concept, 'equalityLevel'>
type 세부정답률필터 = Pick<_Concept, 'minRate' | 'maxRate'>
export type 유형별최대문제수제한필터 = Pick<_PublicWorkbook, 'limitProblemCountPerConcept'>
export type 쌍둥이문제배수필터 = { pairX: NonNullable<_PublicWorkbook['pairX']> | null }
export type 유사문제배수필터 = { similarX: NonNullable<_PublicWorkbook['similarX']> | null }
export type 유사문제난이도필터 = { similarLevel: NonNullable<_PublicWorkbook['similarLevel']> }
type 배점필터 = Pick<PracticeTestWorksheetFilterBody, 'scoreList'>
export type 원본문제포함필터 = Pick<_PublicWorkbook, 'includeSameProblem'>

export type 공통지원교재필터 = 쌍둥이문제배수필터 &
  유사문제배수필터 &
  유사문제난이도필터 &
  모의고사포함여부필터 &
  문제타입필터 &
  기존출제문제제외필터 &
  기존출제문제제외기준필터 &
  교육과정외유형제외필터 &
  원본문제포함필터

type _지원교재필터 = 공통지원교재필터 & {
  workbookId: Workbook['id']
  workbookProblemIdList: StudentWorkbookScoredProblemDetail['workbookPageId'][]
  exampleProblemIds: PublicWorkbookWorksheetFilterBody['exampleProblemIds']
}

export module 학습지필터 {
  export type 단원유형별필터 = 필터아이디필터 &
    문제수필터 &
    난이도필터 &
    난이도설정필터 &
    문제타입필터 &
    모의고사포함여부필터 &
    기존출제문제제외필터 &
    교육과정외유형제외필터 &
    문제수균등배분필터 &
    세부정답률필터

  export type 지원교재필터 = 필터아이디필터 & _지원교재필터 & 유형별최대문제수제한필터
  export type 수능모의고사필터 = 필터아이디필터 &
    배점필터 &
    문제타입필터 &
    교육과정외유형제외필터 & {
      practiceProblemIdList: number[] // 모의고사 문제 범위 (문제 id[])
      curriculumIdList: number[] // 단원 범위. 서버가 null 을 받지 않는다
    }
  export type 학교별기출필터 = 필터아이디필터 &
    유형별최대문제수제한필터 &
    공통지원교재필터 &
    Pick<
      SchoolTestWorksheetFilterBody,
      | 'conceptIdList'
      | 'schoolCodes'
      | 'schoolTestYears'
      | 'schoolTestType'
      | 'schoolTestSchoolType'
      | 'schoolTestRevision'
      | 'schoolTestGrade'
      | 'schoolTestSemester'
    >
  export type 병합학습지필터 = MergedWorksheetFilterBody
  export type 나의DB원본필터 = MyDbOriginalWorksheetFilterBody
  export type 나의DB유사필터 = MyDbSimilarWorksheetFilterBody
}
